import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Brain, Wine, Heart, CheckCircle, ArrowRight, Phone, Shield, Users, Stethoscope } from "lucide-react";

const MAIN_SERVICES = [
  {
    id: "neuropsychiatric",
    title: "Neuropsychiatric Disorders",
    icon: Brain,
    overview: "Specialized care for conditions at the interface of neurology and psychiatry, including dementia, delirium, and other brain-related psychiatric disorders.",
    conditions: [
      { name: "Dementia & Alzheimer's Disease", desc: "Comprehensive evaluation, medication management (Donepezil, Memantine), and caregiver support for all types of dementia.", slug: "dementia" },
      { name: "Delirium", desc: "Acute management of delirium including identification of underlying causes and targeted treatment protocols." },
      { name: "Vascular Dementia", desc: "Diagnosis and management of cognitive decline related to cerebrovascular disease." },
      { name: "Lewy Body Dementia", desc: "Specialized care for this complex condition requiring careful medication management." },
      { name: "Frontotemporal Dementia", desc: "Behavioral and language variant management with family support." },
      { name: "Cognitive Assessment", desc: "Detailed neuropsychological evaluation including MMSE, MoCA, and comprehensive cognitive testing." },
    ],
    approach: "Our neuropsychiatric approach combines thorough diagnostic evaluation with advanced neuroimaging interpretation, evidence-based pharmacotherapy, cognitive rehabilitation, and family/caregiver education and support.",
  },
  {
    id: "de-addiction",
    title: "Drug De-addiction & Rehabilitation",
    icon: Wine,
    overview: "Punjab Government approved drug de-addiction programmes. Complete spectrum of care from medical detoxification to long-term rehabilitation and relapse prevention.",
    conditions: [
      { name: "Alcohol Addiction", desc: "Medical detox, medication management (Naltrexone, Disulfiram, Acamprosate), counseling, and long-term sobriety support.", slug: "alcohol-addiction" },
      { name: "Opioid Dependence", desc: "Buprenorphine/Naloxone maintenance therapy, supervised detox, and comprehensive rehabilitation.", slug: "drug-addiction" },
      { name: "Cannabis Use Disorder", desc: "Treatment for cannabis dependency including motivational enhancement therapy and relapse prevention." },
      { name: "Sedative/Hypnotic Dependence", desc: "Safe, medically supervised tapering and withdrawal management for benzodiazepine and sleeping pill dependence." },
      { name: "Tobacco/Nicotine Cessation", desc: "Pharmacological aids (NRT, Bupropion, Varenicline) combined with behavioral counseling." },
      { name: "Polydrug Dependence", desc: "Integrated treatment for multiple substance dependencies with individualized detox protocols." },
    ],
    approach: "Understanding that addiction involves both physical and psychological dependency is crucial. Physical dependence develops as the body adapts to the substance, while psychological dependence involves the emotional and mental reliance. Our program addresses both through medical detox, psychotherapy (CBT, motivational interviewing), family therapy, 12-step facilitation, and aftercare planning.",
  },
  {
    id: "sexual-health",
    title: "Sexual Health & Sexology",
    icon: Heart,
    overview: "Certified sexologist (American Board & College of Sexologists) providing confidential, compassionate care for all sexual health concerns. Evidence-based treatment in a non-judgmental environment.",
    conditions: [
      { name: "Erectile Dysfunction", desc: "Comprehensive evaluation to identify physical vs. psychological causes. Treatment with PDE5 inhibitors, psychosexual therapy, and lifestyle modifications.", slug: "erectile-dysfunction" },
      { name: "Premature Ejaculation", desc: "On-demand and daily treatment options including Dapoxetine, SSRIs, behavioral techniques, and couples therapy.", slug: "premature-ejaculation" },
      { name: "Performance Anxiety", desc: "Cognitive behavioral approaches to break the anxiety-failure cycle, combined with temporary pharmacological support.", slug: "performance-anxiety" },
      { name: "Low Libido / Desire Disorders", desc: "Hormonal evaluation and treatment, addressing psychological and relationship factors contributing to reduced desire." },
      { name: "Relationship & Marital Issues", desc: "Couples counseling addressing sexual and emotional intimacy, communication, and conflict resolution." },
      { name: "Other Sexual Dysfunctions", desc: "Treatment for delayed ejaculation, orgasmic disorders, and other sexual health concerns." },
    ],
    approach: "Sexual health is an integral part of overall well-being. Our approach combines medical evaluation (hormonal testing, vascular assessment), evidence-based pharmacotherapy, psychosexual therapy, and couples counseling to address both physical and psychological aspects of sexual dysfunction.",
  },
];

const ADDITIONAL_SERVICES = [
  "Psychiatric Evaluation & Diagnosis",
  "Cognitive Behavioral Therapy (CBT)",
  "Psychotherapy & Counselling",
  "Geriatric Psychiatry",
  "Child & Adolescent Psychiatry",
  "Sleep Disorder Management",
  "Psychopharmacology & Medication Management",
  "Behaviour Modification Therapy",
  "Inpatient Psychiatric Care",
  "Online / Teleconsultation",
  "Grief Counselling",
  "Relapse Prevention Programme",
  "Memory & Cognitive Assessment",
  "Marital & Relationship Counselling",
  "Stress Management Programs",
  "Occupational Difficulty Counselling",
];

export default function ServicesPage() {
  return (
    <div>
      <section className="py-16 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" data-testid="services-heading">
            Our Services
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Comprehensive psychiatric care across three core specializations, backed by 28+ years of clinical expertise and internationally recognized certifications.
          </p>
        </div>
      </section>

      {MAIN_SERVICES.map((service, idx) => (
        <section key={service.id} className={`py-16 sm:py-24 ${idx % 2 === 1 ? "bg-secondary/30" : ""}`} data-testid={`service-${service.id}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-primary/10">
                <service.icon className="w-7 h-7 text-primary" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{service.title}</h2>
            </div>
            <p className="text-base text-muted-foreground max-w-3xl mb-8 leading-relaxed">{service.overview}</p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-8">
              {service.conditions.map((c) => (
                <Card key={c.name} className="border hover:shadow-md transition-all duration-300">
                  <CardContent className="p-5">
                    <h3 className="font-heading font-semibold mb-2">
                      {c.slug ? <Link to={`/conditions/${c.slug}`} className="hover:text-primary transition-colors">{c.name}</Link> : c.name}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="bg-card border rounded-xl p-6 sm:p-8">
              <h3 className="font-heading font-semibold text-lg mb-3">Our Treatment Approach</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{service.approach}</p>
            </div>
          </div>
        </section>
      ))}

      {/* Additional Services */}
      <section className="py-16 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-8">Additional Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {ADDITIONAL_SERVICES.map((s) => (
              <div key={s} className="flex items-center gap-2 p-3 rounded-lg bg-card border">
                <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
            Need Help Choosing the Right Service?
          </h2>
          <p className="text-base opacity-90 mb-8">
            Call us and our team will guide you to the right treatment for your needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+919915096633" data-testid="services-call-btn">
              <Button size="lg" variant="secondary" className="gap-2 rounded-full">
                <Phone className="w-5 h-5" /> Call +91-99150 96633
              </Button>
            </a>
            <Link to="/contact" data-testid="services-book-btn">
              <Button size="lg" variant="outline" className="gap-2 rounded-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                Book Appointment <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
